import React, { useState } from "react";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaFileInput from "../../../../ui-kit/kuika-file-input/kuika-file-input";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import { ShowcaseFrame } from "../_layout/showcase-frame";
import { SpecRow } from "../_layout/spec-row";
import { VariantGrid } from "../_layout/variant-grid";

type FileInputVariant = "standalone" | "cell";
type FileInputSize = "sm" | "md";

const VARIANTS: FileInputVariant[] = ["standalone", "cell"];
const SIZES: FileInputSize[] = ["sm", "md"];

const describeFiles = (files: File[]): string => files.map((file: File): string => file.name).join(", ");

// A table-editor cell is 32px tall; the cell variant sits inside one.
const CellFrame: React.FC<React.PropsWithChildren<unknown>> = ({
  children
}: React.PropsWithChildren<unknown>): JSX.Element => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      width: 220,
      height: 32,
      border: "1px solid var(--dark-3)"
    }}
  >
    {children}
  </div>
);

const PickableInput: React.FC = (): JSX.Element => {
  const [picked, setPicked] = useState<File | null>(null);
  const valueLabel: string | undefined = picked === null ? undefined : picked.name;

  return <KuikaFileInput emptyLabel="Choose .xlsx file" accept=".xlsx" valueLabel={valueLabel} onChange={setPicked} />;
};

const LoggedDropzone: React.FC = (): JSX.Element => {
  const [accepted, setAccepted] = useState<string>("—");
  const [turnedAway, setTurnedAway] = useState<string>("—");

  // A mixed drop fires both callbacks, so each keeps its own line.
  const handleFiles = (files: File[]): void => setAccepted(describeFiles(files));
  const handleReject = (files: File[]): void => setTurnedAway(describeFiles(files));

  return (
    <div className="lab-stack" style={{ width: 360, gap: 8 }}>
      <KuikaFileInput variant="dropzone" accept="image/*" multiple onFiles={handleFiles} onReject={handleReject}>
        <KuikaSvg name="upload" width={16} height={16} />
        <span>Drop images here or click to browse</span>
      </KuikaFileInput>
      <span style={{ color: "var(--lab-ink-muted)" }}>Accepted: {accepted}</span>
      <span style={{ color: "var(--lab-ink-muted)" }}>Turned away: {turnedAway}</span>
    </div>
  );
};

export const FileInputStory: React.FC = (): JSX.Element => {
  return (
    <ShowcaseFrame
      title="File input"
      componentName="KuikaFileInput"
      sourcePath="ui-kit/kuika-file-input"
      description="File picker over a hidden native input: an outlined control, a flat table-cell trigger, or a dashed drop zone that turns away files outside accept with a shake and a reason."
    >
      <VariantGrid
        label="variant · size"
        caption="standalone sits on the sm 24 / md 32 control ladder; cell keeps the 24px trigger inside a 32px table cell whatever the size"
        rows={VARIANTS}
        cols={SIZES}
        cellMinWidth={240}
        render={(variant: string, size: string): React.ReactNode => {
          const input: JSX.Element = (
            <KuikaFileInput variant={variant as FileInputVariant} size={size as FileInputSize} />
          );
          return variant === "cell" ? <CellFrame>{input}</CellFrame> : input;
        }}
      />
      <SpecRow
        label="States"
        caption="with a value (and the clear button), disabled, and busy while the caller reads the file"
        inline
      >
        <PickableInput />
        <KuikaFileInput valueLabel="logo.png · 4.2 KB" onChange={(): void => undefined} />
        <KuikaFileInput disabled emptyLabel="Choose file…" />
        <KuikaFileInput busy valueLabel="rows.xlsx" />
      </SpecRow>
      <SpecRow
        label="Dropzone"
        caption="accept=image/* — drop a non-image to see the shake and the notice; the content grows slightly while a file is over the zone"
      >
        <LoggedDropzone />
      </SpecRow>
      <SpecRow
        label="Dropzone with a call to action"
        caption="the button inside keeps its own hover; disabled on the right"
        inline
      >
        <div style={{ width: 240 }}>
          <KuikaFileInput variant="dropzone" accept="image/*">
            <KuikaButton type="secondary" size="sm" uppercase>
              <KuikaSvg name="upload" width={12} height={12} />
              Upload
            </KuikaButton>
          </KuikaFileInput>
        </div>
        <div style={{ width: 240 }}>
          <KuikaFileInput variant="dropzone" accept="image/*" disabled>
            <KuikaButton type="secondary" size="sm" uppercase>
              <KuikaSvg name="upload" width={12} height={12} />
              Upload
            </KuikaButton>
          </KuikaFileInput>
        </div>
      </SpecRow>
    </ShowcaseFrame>
  );
};
